import { apiClient, ApiError, subscribeToApiFailures, type components } from '@boothiva/api-client';
import { notify, PageLoading, PermissionGate } from '@boothiva/ui';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren, type ReactNode } from 'react';
import { Navigate } from 'react-router';

export type Session = components['schemas']['SessionResponse'];
export type TenantSummary = components['schemas']['TenantSummary'];
export const sessionQueryKey = ['auth', 'session'] as const;

export function useSessionQuery() {
  return useQuery({
    queryKey: sessionQueryKey,
    queryFn: async (): Promise<Session | null> => {
      try {
        return await apiClient.get<Session>('/api/v1/auth/session');
      } catch (error) {
        if (error instanceof ApiError && error.status === 401) return null;
        throw error;
      }
    },
    retry: false,
    staleTime: 30_000
  });
}

type SessionContextValue = {
  session: Session | null;
  isLoading: boolean;
  /** True when the session check itself failed for a reason other than "not logged in" (e.g. a
   * transient 429/500/network error) — the caller shouldn't treat this the same as a confirmed
   * logged-out state (that's `session === null` with `isError` false). */
  isError: boolean;
  refetch: ReturnType<typeof useSessionQuery>['refetch'];
};
const SessionContext = createContext<SessionContextValue | null>(null);
const anonymousAuthPaths = new Set([
  '/api/v1/auth/login', '/api/v1/auth/register', '/api/v1/auth/session',
  '/api/v1/auth/verify-email', '/api/v1/auth/resend-verification',
  '/api/v1/auth/forgot-password', '/api/v1/auth/reset-password'
]);

export function SessionProvider({ children, loginPath = '/login' }: PropsWithChildren<{ loginPath?: string }>) {
  const query = useSessionQuery();
  const queryClient = useQueryClient();
  useEffect(() => subscribeToApiFailures((error, context) => {
    if (error.status === 401 && !anonymousAuthPaths.has(context.path)) {
      queryClient.setQueryData(sessionQueryKey, null);
      if (window.location.pathname !== loginPath) window.location.assign(loginPath);
    }
    if (error.status === 403) notify.error("You don't have permission to do that.", error.problem.correlationId);
  }), [loginPath, queryClient]);
  const value = useMemo(
    () => ({ session: query.data ?? null, isLoading: query.isLoading, isError: query.isError, refetch: query.refetch }),
    [query.data, query.isLoading, query.isError, query.refetch]
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used within SessionProvider.');
  return value;
}

export function usePermissions() {
  const { session } = useSession();
  const permissions = useMemo(() => new Set(session?.permissions ?? []), [session?.permissions]);
  return useMemo(() => ({ has: (code: string) => permissions.has(code) }), [permissions]);
}

export function useEntitlements() {
  const { session } = useSession();
  const features = session?.features;
  return useMemo(() => ({ has: (featureKey: string) => features?.[featureKey] === true }), [features]);
}

export function useEntitlement(featureKey: string) {
  const { has } = useEntitlements();
  return has(featureKey);
}

/**
 * A displayable src for the signed-in user's profile photo, or null while it loads or when there is none.
 *
 * The photo is fetched through the API client rather than pointed at by an <img>: the API tells the customer and admin
 * portals' session cookies apart by the X-Boothiva-Portal header, which an <img> cannot send, so a plain link was
 * answered 401 and every avatar fell back to initials. Each photo version (?v=) is fetched once and cached.
 */
export function useAvatarSrc(avatarUrl: string | null | undefined): string | null {
  const photo = useQuery({
    queryKey: ['auth', 'avatar', avatarUrl],
    queryFn: () => apiClient.getBlob(avatarUrl!),
    enabled: Boolean(avatarUrl),
    staleTime: Infinity,
    retry: false,
  });
  const blob = avatarUrl ? photo.data : undefined;
  const [src, setSrc] = useState<string | null>(null);
  useEffect(() => {
    if (!blob) { setSrc(null); return undefined; }
    const objectUrl = URL.createObjectURL(blob);
    setSrc(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [blob]);
  return blob ? src : null;
}

export function useLogout(loginPath = '/login') {
  const queryClient = useQueryClient();
  return async () => {
    try { await apiClient.post<void>('/api/v1/auth/logout'); }
    finally { queryClient.setQueryData(sessionQueryKey, null); window.location.assign(loginPath); }
  };
}

export function RequireAuth({ children, loginPath = '/login', suspendedFallback }: PropsWithChildren<{
  loginPath?: string;
  /** Rendered instead of `children` when the signed-in tenant is Suspended. Opt-in only — omitting it
   * (the admin app's case) reproduces today's behaviour exactly, regardless of what a platform-scoped
   * session's `tenant` field happens to contain. */
  suspendedFallback?: (tenant: TenantSummary) => ReactNode;
}>) {
  const { session, isLoading, isError, refetch } = useSession();
  if (isLoading) return <PageLoading />;
  // A transient failure (429, 500, network) is not a confirmed "not logged in" — only a session
  // check that actually resolved (successfully, to null) means the server said 401.
  if (isError) {
    return <main className="bv-empty" role="alert">
      <h1>Couldn't reach Boothiva</h1>
      <p>Something went wrong checking your session. This is usually temporary.</p>
      <button type="button" className="bv-button" onClick={() => refetch()}>Try again</button>
    </main>;
  }
  if (!session) return <Navigate to={loginPath} replace />;
  if (suspendedFallback && session.tenant.status === 'Suspended') return <>{suspendedFallback(session.tenant)}</>;
  return <>{children}</>;
}

export function RequirePermission({ code, children }: PropsWithChildren<{ code: string }>) {
  const { has } = usePermissions();
  if (!has(code)) return <main className="bv-empty"><h1>403</h1><p>You don't have permission to view this page.</p></main>;
  return <PermissionGate permission={code} has={has}>{children}</PermissionGate>;
}

/** Placeholder for the deferred idle-timeout countdown and renewal UX. */
export function useIdleWarning(minutesUntilWarn: number): void {
  void minutesUntilWarn;
  // Intentionally empty until the idle-timeout milestone.
}
